import type { RouteLocationNormalizedLoaded } from "vue-router";
import { AppIdentifier } from "@/shared/@types/AppIdentifier";
import { AppRouteNames } from "@/shared/@types/AppRouteNames";
import { FeatureFlag } from "@/shared/@types/FeatureFlag";
import { usePermission } from "@/shared/composables/usePermission.composable";
import { useFeatureFlagsStore } from "@/shared/stores/FeatureFlagsStore";

const PageNotFoundView = () =>
  import("@/shared/views/PageNotFoundView/PageNotFoundView.vue");
const UsersView = () =>
  import("@/app/wid-account-management-app/users/UsersView/UsersView.vue");
const InvitationsView = () =>
  import("@/app/wid-account-management-app/invitations/InvitationsView/InvitationsView.vue");
const OverviewView = () =>
  import("@/app/wid-account-management-app/overview/OverviewView/OverviewView.vue");
const ServicesView = () =>
  import("@/app/wid-account-management-app/services/ServicesView/ServicesView.vue");
const SupportCasesView = () =>
  import("@/app/wid-account-management-app/support-cases/SupportCasesView/SupportCasesView.vue");
const SettingsView = () =>
  import("@/app/wid-account-management-app/settings/SettingsView/SettingsView.vue");

const redirectIfNotAccountManager = () => {
  const { hasPermissionForApp } = usePermission();
  if (hasPermissionForApp(AppIdentifier.WestfalenIdAccountManagement)) {
    return true;
  }

  // Redirect to overview if not an account manager
  return { name: AppRouteNames.WestfalenIdAccountManagementOverview };
};

const accountManagerOnlyRoutes = [
  {
    path: "users",
    name: AppRouteNames.WestfalenIdAccountManagementUsers,
    props: (route: RouteLocationNormalizedLoaded) => ({
      userQueryId: route.query.userId,
    }),
    component: UsersView,
  },
  {
    path: "invitations",
    name: AppRouteNames.WestfalenIdAccountManagementInvitations,
    props: (route: RouteLocationNormalizedLoaded) => ({
      invitationQueryId: route.query.invitationId,
    }),
    component: InvitationsView,
  },
  {
    path: "services",
    name: AppRouteNames.WestfalenIdAccountManagementServices,
    component: ServicesView,
  },
  {
    path: "support-cases",
    name: AppRouteNames.WestfalenIdAccountManagementSupportCases,
    props: (route: RouteLocationNormalizedLoaded) => ({
      supportCaseQueryNr: route.query.caseNumber,
    }),
    component: SupportCasesView,
  },
  {
    path: "settings",
    name: AppRouteNames.WestfalenIdAccountManagementSettings,
    component: SettingsView,
    beforeEnter: () =>
      useFeatureFlagsStore().isFeatureEnabled(FeatureFlag.WidAccessSettings)
        ? true
        : AppRouteNames.WestfalenIdAccountNotFound,
  },
].map((route) => ({
  ...route,
  beforeEnter: redirectIfNotAccountManager,
}));

export const routes = [
  {
    path: "",
    name: AppRouteNames.WestfalenIdAccountManagementOverview,
    component: OverviewView,
  },
  ...accountManagerOnlyRoutes,
  {
    path: ":pathMatch(.*)*",
    name: AppRouteNames.WestfalenIdAccountNotFound,
    component: PageNotFoundView,
    props: {
      homeRouteName: AppRouteNames.WestfalenIdAccountManagementUsers,
    },
  },
];
